import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import TopBar from '@/components/TopBar';
import { Card, CardSm, Pill } from '@/components/ui/Form';
import { Button } from '@/components/ui/Button';
import { Ruler, Pulse, TreeStructure, MapPin } from '@phosphor-icons/react';
import { fmt, healthClassToColor } from '@/lib/utils';

export default function TreeDetail() {
  const { treeId } = useParams();
  const nav = useNavigate();
  const [tree, setTree] = useState(null);

  useEffect(() => {
    api.get(`/trees/${treeId}`).then((r) => setTree(r.data));
  }, [treeId]);

  if (!tree) return <div className="p-4">Loading…</div>;
  const events = tree.measurement_events || [];

  return (
    <div data-testid="tree-detail">
      <TopBar title={tree.tree_code || 'Tree'} back />
      <div className="p-4 space-y-4">
        <Card>
          <div className="flex items-center gap-2 mb-2">
            <TreeStructure size={20} weight="bold" />
            <h2 className="font-display font-bold">{tree.species_scientific || 'Unidentified'}</h2>
          </div>
          <p className="text-xs text-carbon-soft mono mb-1">{tree.species_common}</p>
          <div className="flex items-center gap-2 text-xs mono">
            <MapPin size={14} />
            <span>{tree.gps_lat.toFixed(5)}, {tree.gps_lon.toFixed(5)}</span>
            <span className="text-carbon-soft">·</span>
            <span>±{tree.gps_accuracy_m?.toFixed(1)} m</span>
          </div>
          {tree.latest_health_class && (
            <div className="mt-2">
              <Pill color={healthClassToColor(tree.latest_health_class)}>{tree.latest_health_class}</Pill>
            </div>
          )}
        </Card>

        <div className="grid grid-cols-2 gap-2">
          <Button as={Link} to={`/trees/${treeId}/measure`} className="w-full" data-testid="add-measurement-btn">
            <Ruler size={18} weight="bold" /> Measure
          </Button>
          <Button as={Link} to={`/trees/${treeId}/health`} variant="dark" className="w-full" data-testid="add-health-btn">
            <Pulse size={18} weight="bold" /> Health
          </Button>
        </div>

        <h3 className="font-display font-bold tracking-tight">Measurement History</h3>
        {events.length === 0 ? (
          <CardSm><p className="text-sm text-carbon-soft">No measurements yet.</p></CardSm>
        ) : (
          <ul className="space-y-2" data-testid="event-history">
            {events.map((e) => (
              <li key={e.id} className="card-hard-sm p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-display font-bold text-sm uppercase tracking-widest">{e.event_type}</span>
                  {e.canopy_health_class && (
                    <Pill color={healthClassToColor(e.canopy_health_class)}>{e.canopy_health_class}</Pill>
                  )}
                </div>
                <p className="mono text-xs text-carbon-soft mb-2">
                  {new Date(e.event_date).toLocaleString()}
                </p>
                <div className="grid grid-cols-2 gap-1 text-xs mono">
                  <span>DBH: <b>{(e.dbh_cm || []).join(', ') || '—'} cm</b></span>
                  <span>Height: <b>{fmt(e.height_total_m, 2)} m</b></span>
                  <span>AGB: <b>{fmt(e.agb_kg, 1)} kg</b></span>
                  <span>tCO₂e: <b>{fmt(e.carbon_stock_tco2e, 4)}</b></span>
                </div>
                {e.disease_flags?.length > 0 && (
                  <p className="text-xs mt-2">⚠ {e.disease_flags.join(', ')}</p>
                )}
                <p className="mono text-[10px] text-carbon-tertiary mt-2 truncate" title={e.data_hash_sha256}>
                  hash: {e.data_hash_sha256?.slice(0, 16)}…
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
